'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Phone, X, Star as StarIcon } from 'lucide-react';
import siteTheme from '@/lib/theme';
import type { SiteSettings, CmsHeader, CmsImage, CmsActionItem } from '@/lib/cms';
const { colors, fonts } = siteTheme;


/* ─── Hamburger ───────────────────────────────────────────────────────────── */
function Hamburger({ open, color = '#fff' }: { open: boolean; color?: string }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      alignItems: 'flex-end', gap: '5px',
      width: '2.8rem', height: '2.8rem', cursor: 'pointer',
    }}>
      <span style={{
        display: 'block', width: open ? '2.2rem' : '2.2rem', height: '1px', background: color,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        transform: open ? 'translateY(6px) rotate(45deg)' : 'none',
      }} />
      <span style={{
        display: 'block', width: '1.4rem', height: '1px', background: color,
        transition: 'opacity 0.3s ease, width 0.3s ease',
        opacity: open ? 0 : 1,
      }} />
      <span style={{
        display: 'block', width: '2.2rem', height: '1px', background: color,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        transform: open ? 'translateY(-6px) rotate(-45deg)' : 'none',
      }} />
    </div>
  );
}

/* ─── Hero Brand Crest (left-aligned, large, white) ─────────────────────── */
function HeroCrest({ siteName, tagline, logoWhite }: { siteName: string; tagline?: string | null; logoWhite?: CmsImage | null }) {
  return (
    <Link href="/" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.3rem', textDecoration: 'none' }}>
      {logoWhite ? (
        <div style={{ position: 'relative', height: 'clamp(5rem, 5vw, 9rem)', width: 'clamp(9rem, 10vw, 14rem)' }}>
          <Image src={logoWhite.url} alt={logoWhite.alternativeText || siteName} fill sizes="(max-width: 768px) 144px, 224px" style={{ objectFit: 'contain', objectPosition: 'left' }} />
        </div>
      ) : (
        <>
          {/* Crown SVG */}
          <svg width="44" height="22" viewBox="0 0 60 28" fill="none" style={{ marginBottom: '0.2rem' }}>
            <path d="M5 28 L5 14 L18 4 L30 14 L42 4 L55 14 L55 28" stroke="rgba(255,255,255,0.9)" strokeWidth="1.2" fill="none" />
            <circle cx="5" cy="14" r="2.5" fill="rgba(255,255,255,0.9)" />
            <circle cx="30" cy="4" r="2.5" fill="rgba(255,255,255,0.9)" />
            <circle cx="55" cy="14" r="2.5" fill="rgba(255,255,255,0.9)" />
          </svg>
          {/* Monogram */}
          <div style={{ fontFamily: fonts.serif, fontSize: '4.8rem', fontWeight: 300, letterSpacing: '0.08em', color: '#fff', lineHeight: 1 }}>SR</div>
          {/* 5 stars */}
          <div style={{ display: 'flex', gap: '0.3rem', margin: '0.2rem 0' }}>
            {[1, 2, 3, 4, 5].map((s) => (
              <StarIcon key={s} size={9} fill="rgba(255,255,255,0.85)" color="rgba(255,255,255,0.85)" strokeWidth={0} />
            ))}
          </div>
          {/* Script name */}
          <div style={{ fontFamily: fonts.script, fontSize: '2.2rem', fontWeight: 400, color: '#fff', lineHeight: 1 }}>{siteName}</div>
          {/* Tagline */}
          {tagline && <div style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', lineHeight: 1, marginTop: '0.1rem' }}>{tagline}</div>}
        </>
      )}
    </Link>
  );
}

/* ─── Sticky Brand (compact, centered, dark text) ────────────────────────── */
function StickyCenterBrand({ siteName, tagline, logo }: { siteName: string; tagline?: string | null; logo?: CmsImage | null }) {
  return (
    <Link href="/" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem', textDecoration: 'none' }}>
      {logo ? (
        <div style={{ position: 'relative', height: '4.8rem', width: '14rem' }}>
          <Image src={logo.url} alt={logo.alternativeText || siteName} fill sizes="224px" style={{ objectFit: 'contain' }} />
        </div>
      ) : (
        <>
          <div style={{ fontFamily: fonts.script, fontSize: '3rem', fontWeight: 400, color: colors.navyDeep, lineHeight: 1 }}>{siteName}</div>
          {tagline && <div style={{ fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400, letterSpacing: '0.3em', textTransform: 'uppercase', color: colors.goldLight, lineHeight: 1 }}>{tagline}</div>}
        </>
      )}
    </Link>
  );
}

/* ─── Sticky Left Symbol (compact crest) ─────────────────────────────────── */
function StickyLeftSymbol({ logo }: { logo?: CmsImage | null }) {
  return (
    <Link href="/" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem', textDecoration: 'none' }}>
      {logo ? (
        <div style={{ position: 'relative', height: '4rem', width: '4rem' }}>
          <Image src={logo.url} alt="Logo" fill sizes="64px" style={{ objectFit: 'contain' }} />
        </div>
      ) : (
        <>
          <div style={{ fontFamily: fonts.serif, fontSize: '2.4rem', fontWeight: 300, letterSpacing: '0.1em', color: colors.gold, lineHeight: 1 }}>SR</div>
          <div style={{ display: 'flex', gap: '0.2rem' }}>
            {[1, 2, 3, 4, 5].map((s) => (
              <StarIcon key={s} size={7} fill={colors.goldPale} color={colors.goldPale} strokeWidth={0} />
            ))}
          </div>
        </>
      )}
    </Link>
  );
}

/* ─── Full-screen overlay menu ────────────────────────────────────────────── */
function OverlayMenu({ open, onClose, phone, actions, navLinks, bottomLinks, logoCompact, overlayImages }: {
  open: boolean;
  onClose: () => void;
  phone: string | null;
  actions: CmsActionItem[];
  navLinks: { label: string; href: string }[];
  bottomLinks: { label: string; href: string }[];
  logoCompact: CmsImage | null;
  overlayImages?: CmsImage[];
}) {
  const images = overlayImages && overlayImages.length > 0 ? overlayImages : [];
  const [activeIdx, setActiveIdx] = useState(0);

  // Auto-play: advance image every 4 seconds when menu is open and there are multiple images
  useEffect(() => {
    if (!open || images.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIdx((i) => (i + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [open, images.length]);

  // Reset to first image when menu closes
  useEffect(() => {
    if (!open) setActiveIdx(0);
  }, [open]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      display: 'flex', flexDirection: 'column',
      opacity: open ? 1 : 0,
      transform: open ? 'translateY(0)' : 'translateY(-2rem)',
      transition: 'opacity 0.4s ease, transform 0.4s ease',
      pointerEvents: open ? 'auto' : 'none',
      overflow: 'hidden',
    }}>

      {/* ── Top utility bar ── */}
      <div style={{
        flexShrink: 0, zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 clamp(2rem, 4vw, 5rem)',
        height: '7rem',
        backgroundColor: colors.bgAlt,
        borderBottom: `1px solid ${colors.goldPale}`,
      }}>
        {/* Phone with number */}
        {phone ? (
          <a href={`tel:${phone.replace(/\s/g, '')}`} style={{
            fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
            letterSpacing: '0.06em', color: colors.goldLight, textDecoration: 'none',
            display: 'flex', alignItems: 'center', gap: '0.9rem',
          }}>
            <Phone size={16} strokeWidth={1.5} />
            {phone}
          </a>
        ) : <span />}

        {/* Right: text_link actions + close X */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '3rem' }}>
          {actions.filter(a => a.type === 'text_link').map(a => (
            <Link key={a.id} href={a.url || '#'} onClick={onClose} style={{
              fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: a.text_color || colors.goldLight, textDecoration: 'none',
            }}>{a.text}</Link>
          ))}
          <div onClick={onClose} style={{ cursor: 'pointer', color: colors.goldLight, lineHeight: 1 }}>
            <X size={40} strokeWidth={2} aria-label="Close menu" />
          </div>
        </div>
      </div>

      {/* ── Main body: left image + right nav ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* LEFT: resort image panel — desktop only */}
        <div className="hidden lg:block" style={{ flex: '0 0 60%', position: 'relative', overflow: 'hidden' }}>
          {images.length > 0 ? (
            <>
              {/* Slideshow: stack images, fade active one in */}
              {images.map((img, idx) => (
                <div key={img.id} style={{
                  position: 'absolute', inset: 0,
                  opacity: idx === activeIdx ? 1 : 0,
                  transition: 'opacity 0.8s ease',
                }}>
                  <Image src={img.url} alt={img.alternativeText || 'Sandy Resort'} fill sizes="60vw" style={{ objectFit: 'cover' }} />
                </div>
              ))}
              {/* Dot indicators — only when more than one image */}
              {images.length > 1 && (
                <div style={{
                  position: 'absolute', bottom: '2.4rem', left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex', gap: '0.8rem', zIndex: 2,
                }}>
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveIdx(idx)}
                      style={{
                        width: idx === activeIdx ? '2rem' : '0.6rem',
                        height: '0.6rem',
                        borderRadius: '0.3rem',
                        background: idx === activeIdx ? colors.gold : 'rgba(255,255,255,0.45)',
                        border: 'none', padding: 0, cursor: 'pointer',
                        transition: 'width 0.3s ease, background 0.3s ease',
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{
              position: 'absolute', inset: 0,
              background: `linear-gradient(160deg, ${colors.navyDeep} 0%, #0d2e3f 45%, #091e2b 100%)`,
            }}>
              {/* Decorative overlay pattern */}
              <div style={{
                position: 'absolute', inset: 0, opacity: 0.06,
                backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0, rgba(255,255,255,0.5) 1px, transparent 0, transparent 50%)',
                backgroundSize: '24px 24px',
              }} />
              {/* Large monogram watermark */}
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  fontFamily: fonts.serif, fontSize: '28rem', fontWeight: 300,
                  letterSpacing: '0.04em', color: 'rgba(255,255,255,0.04)', lineHeight: 1,
                  userSelect: 'none', pointerEvents: 'none',
                }}>SR</div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: nav panel */}
        <div style={{
          flex: 1, backgroundColor: colors.bg,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '3rem 2.4rem',
          overflowY: 'auto',
        }}>
          {/* Logo / crest */}
          <Link href="/" onClick={onClose} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: '0.3rem', textDecoration: 'none', marginBottom: '3rem',
          }}>
            {logoCompact ? (
              <div style={{ position: 'relative', height: '6rem', width: '6rem' }}>
                <Image src={logoCompact.url} alt="Logo" fill sizes="96px" style={{ objectFit: 'contain' }} />
              </div>
            ) : (
              <>
                <svg width="32" height="16" viewBox="0 0 60 28" fill="none">
                  <path d="M5 28 L5 14 L18 4 L30 14 L42 4 L55 14 L55 28" stroke={colors.goldPale} strokeWidth="1.2" fill="none" />
                  <circle cx="5" cy="14" r="2" fill={colors.goldPale} />
                  <circle cx="30" cy="4" r="2" fill={colors.goldPale} />
                  <circle cx="55" cy="14" r="2" fill={colors.goldPale} />
                </svg>
                <div style={{ fontFamily: fonts.serif, fontSize: '3rem', fontWeight: 300, letterSpacing: '0.1em', color: colors.gold, lineHeight: 1 }}>SR</div>
                <div style={{ display: 'flex', gap: '0.25rem', margin: '0.3rem 0' }}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <StarIcon key={s} size={7} fill={colors.goldPale} color={colors.goldPale} strokeWidth={0} />
                  ))}
                </div>
              </>
            )}
          </Link>

          {/* Nav links */}
          <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={onClose}
                className="overlay-nav-link"
                style={{
                  display: 'block', textAlign: 'center',
                  fontFamily: fonts.serif,
                  fontSize: 'clamp(1.8rem, 2.2vw, 2.8rem)',
                  fontWeight: 300,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: colors.gold,
                  padding: '0.85rem 0',
                  textDecoration: 'none',
                  lineHeight: 1.2,
                }}
              >
                {link.label}
              </Link>
            ))}

            {/* Decorative star */}
            <div style={{ marginTop: '2.4rem' }}>
              <StarIcon size={10} fill={colors.goldPale} color={colors.goldPale} strokeWidth={0} />
            </div>
          </nav>
        </div>
      </div>

      {/* ── Bottom: secondary links spread across full width ── */}
      {bottomLinks.length > 0 && (
        <div style={{
          flexShrink: 0, zIndex: 2,
          borderTop: `1px solid ${colors.goldPale}`,
          backgroundColor: colors.bg,
          padding: '1.8rem clamp(2rem, 4vw, 5rem)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1.6rem', flexWrap: 'wrap',
        }}>
          {bottomLinks.map((l) => (
            <Link key={l.href} href={l.href} onClick={onClose} className="overlay-bottom-link" style={{
              fontFamily: fonts.sans, fontSize: '1.25rem', fontWeight: 400,
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: colors.gold, textDecoration: 'none',
            }}>{l.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Render a single right-action item ──────────────────────────────────── */
function ActionItem({ action, theme }: { action: CmsActionItem; theme: 'hero' | 'sticky' }) {
  const isHero = theme === 'hero';
  const defaultColor = isHero ? 'rgba(255,255,255,0.8)' : colors.goldLight;
  const borderColor = isHero ? 'rgba(255,255,255,0.25)' : colors.goldPale;
  const divider: React.CSSProperties = {
    paddingRight: '2.4rem',
    borderRight: `1px solid ${borderColor}`,
    marginRight: '2.4rem',
    whiteSpace: 'nowrap',
  };

  switch (action.type) {
    case 'phone':
      return (
        <a href={`tel:${(action.phone_number || '').replace(/\s/g, '')}`} className="hidden lg:block" style={{
          fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 300,
          letterSpacing: '0.05em', color: action.text_color || defaultColor,
          textDecoration: 'none', ...divider,
        }}>
          {action.text || action.phone_number}
        </a>
      );

    case 'text_link':
      return (
        <Link href={action.url || '#'} target={action.open_in_new_tab ? '_blank' : undefined} className="hidden lg:block" style={{
          fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: action.text_color || defaultColor,
          textDecoration: 'none', ...divider,
        }}>
          {action.text}
        </Link>
      );

    case 'button':
      return (
        <Link href={action.url || '#'} target={action.open_in_new_tab ? '_blank' : undefined} className="hidden lg:inline-flex btn-luxury-nav" style={{
          fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          color: action.text_color || '#fff',
          background: action.background || colors.green,
          border: isHero ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent',
          borderRadius: '0.5rem',
          padding: isHero ? '1rem 2.6rem' : '0.9rem 2.4rem',
          whiteSpace: 'nowrap', textDecoration: 'none',
          marginRight: '2.4rem',
          alignItems: 'center',
        }}>
          {action.text}
        </Link>
      );

    case 'label':
      return (
        <span className="hidden lg:block" style={{
          fontFamily: fonts.sans, fontSize: '1.4rem', fontWeight: 400,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: action.text_color || defaultColor, ...divider,
        }}>
          {action.text}
        </span>
      );

    default:
      return null;
  }
}

/* ─── Main Navbar ─────────────────────────────────────────────────────────── */
export default function Navbar({ settings, header }: { settings?: SiteSettings | null; header?: CmsHeader | null }) {
  const actions = header?.right_actions ?? [];
  // Extract phone for mobile icon and overlay menu
  const phoneAction = actions.find(a => a.type === 'phone') ?? null;
  const phone = phoneAction?.phone_number ?? null;

  const siteName = header?.navbar_center?.brand_name || settings?.site_name || siteTheme.name;
  const siteTagline = settings?.tagline ?? null;
  const logoFull      = header?.logos?.logo_full      ?? null;
  const logoCompact   = header?.logos?.logo_compact   ?? null;
  const logoSecondary = header?.logos?.logo_secondary ?? null;
  const centerLogo    = logoSecondary
    ?? (header?.navbar_center?.use_logo && header?.navbar_center?.logo ? header.navbar_center.logo : null);

  const navLinks = header?.nav_links?.filter(l => l.menu_position === 'center') ?? [];
  const bottomLinks = header?.nav_links?.filter(l => l.menu_position === 'bottom') ?? [];
  const announcement = header?.announcement_bar?.enabled ? header.announcement_bar : null;

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      {/* ── Announcement bar ──────────────────────────────────────────────── */}
      {announcement && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 60,
          backgroundColor: colors.navyDeep,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0.7rem 2rem', gap: '1rem',
        }}>
          <p style={{ fontFamily: fonts.sans, fontSize: '1.3rem', fontWeight: 400, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.85)', margin: 0 }}>
            {announcement.text}
          </p>
          {announcement.link_label && announcement.link_url && (
            <a href={announcement.link_url} style={{
              fontFamily: fonts.sans, fontSize: '1.3rem', fontWeight: 500,
              letterSpacing: '0.1em', color: colors.gold, textDecoration: 'underline',
            }}>{announcement.link_label}</a>
          )}
        </div>
      )}

      {/* ── Single header — two inner layers swap on scroll ──────────────── */}
      {/* Mobile Top Header */}
      <div className="flex lg:hidden fixed top-0 left-0 w-full z-[60] px-4 py-2 justify-between items-center text-xs font-sans font-medium" style={{ backgroundColor: colors.gold, color: colors.navyDeep }}>
        {settings?.whatsapp_number && (
          <a href={`tel:${settings.whatsapp_number.replace(/\s/g, '')}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none', color: 'inherit' }}>
            <Phone size={12} /> <span>Mob: {settings.whatsapp_number}</span>
          </a>
        )}
        {settings?.phone && (
          <a href={`tel:${settings.phone.replace(/\s/g, '')}`} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none', color: 'inherit' }}>
            <Phone size={12} /> <span>Tel: {settings.phone}</span>
          </a>
        )}
      </div>

      <header className="top-[32px] lg:top-0" style={{ position: 'fixed', left: 0, width: '100%', zIndex: 50 }}>

        {/* Hero layer: transparent, tall — visible when NOT scrolled */}
        <div
          aria-hidden={scrolled}
          style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            padding: 'clamp(2.5rem, 4vw, 4rem) clamp(2rem, 4vw, 5rem)',
            transition: 'opacity 0.5s ease',
            opacity: scrolled ? 0 : 1,
            pointerEvents: scrolled ? 'none' : 'auto',
            zIndex: scrolled ? 0 : 1,
          }}
        >
          <HeroCrest siteName={siteName} tagline={siteTagline} logoWhite={logoFull} />
          <div style={{ display: 'flex', alignItems: 'center', paddingTop: '0.4rem' }}>
            {actions.map(a => <ActionItem key={a.id} action={a} theme="hero" />)}
            <div onClick={() => setMenuOpen(v => !v)} className="hamburger-wrap" style={{ cursor: 'pointer' }}>
              <Hamburger open={menuOpen} color="#fff" />
            </div>
          </div>
        </div>

        {/* Sticky layer: solid bg, compact — visible when scrolled */}
        <div
          aria-hidden={!scrolled}
          style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            height: '7.6rem',
            backgroundColor: colors.bg,
            borderBottom: `1px solid ${colors.goldPale}`,
            display: 'flex', alignItems: 'center',
            padding: '0 clamp(2rem, 4vw, 5rem)',
            transition: 'opacity 0.5s ease',
            opacity: scrolled ? 1 : 0,
            pointerEvents: scrolled ? 'auto' : 'none',
            zIndex: scrolled ? 1 : 0,
          }}
        >
          {/* DESKTOP LEFT: compact crest */}
          <div className="hidden lg:block" style={{ position: 'absolute', left: 'clamp(2rem, 4vw, 5rem)' }}>
            <StickyLeftSymbol logo={logoCompact} />
          </div>

          {/* MOBILE LEFT: empty (replaced by mobile top header) */}
          <div className="flex lg:hidden" style={{ position: 'absolute', left: 'clamp(2rem, 4vw, 5rem)' }}>
            {/* Keeping spacing intact if needed, or leave empty */}
          </div>

          {/* CENTER */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <div className="hidden lg:flex">
              <StickyCenterBrand siteName={siteName} tagline={siteTagline} logo={centerLogo} />
            </div>
            <Link href="/" className="flex lg:hidden" style={{ flexDirection: 'column', alignItems: 'center', gap: '0.3rem', textDecoration: 'none' }}>
              {logoCompact ? (
                <div style={{ position: 'relative', height: '4.8rem', width: '4.8rem' }}>
                  <Image src={logoCompact.url} alt="Logo" fill sizes="80px" style={{ objectFit: 'contain' }} />
                </div>
              ) : (
                <>
                  <div style={{ fontFamily: fonts.serif, fontSize: '2.4rem', fontWeight: 300, letterSpacing: '0.12em', color: colors.gold, lineHeight: 1 }}>SR</div>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <StarIcon key={s} size={6} fill={colors.goldPale} color={colors.goldPale} strokeWidth={0} />
                    ))}
                  </div>
                </>
              )}
            </Link>
          </div>

          {/* RIGHT */}
          <div style={{ position: 'absolute', right: 'clamp(2rem, 4vw, 5rem)', display: 'flex', alignItems: 'center' }}>
            {actions.map(a => <ActionItem key={a.id} action={a} theme="sticky" />)}
            <div onClick={() => setMenuOpen(v => !v)} className="hamburger-wrap" style={{ cursor: 'pointer' }}>
              <Hamburger open={menuOpen} color={colors.gold} />
            </div>
          </div>
        </div>

        {/* Spacer so the fixed header claims its height in the stacking context */}
        <div style={{ height: '7.6rem' }} aria-hidden="true" />
      </header>

      {/* ── Overlay menu ─────────────────────────────────────────────────── */}
      <OverlayMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        phone={phone}
        actions={actions}
        navLinks={navLinks}
        bottomLinks={bottomLinks}
        logoCompact={logoCompact}
        overlayImages={header?.menu_images ?? []}
      />
    </>
  );
}
